import { LuIcon, Menu, type MenuClickTriggerProps, type MenuRootProps, type MenuVariantProps } from "@overmap-ai/blocks"
import { nameModelSortFn } from "@overmap-ai/common"
import { useSort, useTextFilter } from "@overmap-ai/hooks"
import { memo, type ReactElement } from "react"

import { projectFilterCallback } from "../../filtering/utils"
import { modelIconMapping } from "../../mappings/icons"
import { ProjectSelectGroup, type ProjectSelectGroupProps } from "../MenuSelectGroups/ProjectSelectGroup"

type ProjectSelectProps = Omit<MenuRootProps, "children"> &
	Omit<MenuClickTriggerProps, "children"> &
	MenuVariantProps &
	ProjectSelectGroupProps & {
		children: ReactElement
	}

export const ProjectSelect = memo<ProjectSelectProps>((props) => {
	const {
		// Trigger props
		children,
		disabled = false,
		// MenuContent props
		size,
		radius,
		variant,
		accentColor,
		// ProjectSelectGroup props
		projects,
		required,
		value,
		defaultValue,
		onValueChange,
		// Rest props
		...rest
	} = props

	const sortedProjects = useSort(projects, nameModelSortFn)

	const [filteredProjects, filterValue, setFilterValue] = useTextFilter(sortedProjects, projectFilterCallback)

	return (
		<Menu.Root {...rest}>
			<Menu.ClickTrigger disabled={disabled}>{children}</Menu.ClickTrigger>
			<Menu.Content size={size} radius={radius} variant={variant} accentColor={accentColor}>
				<Menu.InputRoot>
					<Menu.InputSlot>
						<LuIcon icon={modelIconMapping.search} />
					</Menu.InputSlot>
					<Menu.InputField placeholder="Search" value={filterValue} onValueChange={setFilterValue} />
				</Menu.InputRoot>
				{filteredProjects.length > 0 && <Menu.Separator />}
				<Menu.Scroll>
					{required ? (
						<ProjectSelectGroup
							projects={filteredProjects}
							required={required}
							value={value}
							defaultValue={defaultValue}
							onValueChange={onValueChange}
						/>
					) : (
						<ProjectSelectGroup
							projects={filteredProjects}
							required={required}
							value={value}
							defaultValue={defaultValue}
							onValueChange={onValueChange}
						/>
					)}
				</Menu.Scroll>
			</Menu.Content>
		</Menu.Root>
	)
})
ProjectSelect.displayName = "ProjectSelect"
